import { isCohorteFull } from './cohorteCapacityMessage';

export type FiliereAvailabilityStatus = 'open' | 'closed';

export type FiliereAvailability = {
  status: FiliereAvailabilityStatus;
  label: string;
};

export type FiliereCohorteAvailabilityRow = {
  idFiliere?: string;
  filiere?: { id?: string };
  statut?: string;
  capacity?: { placesRestantes?: number; estComplete?: boolean };
};

/** Aligné backend : OUVERTE (avant début) + EN_COURS (session démarrée). */
export function isCohorteOpenForInscription(statut: string | null | undefined): boolean {
  const s = String(statut ?? '').toUpperCase();
  return s === 'OUVERTE' || s === 'EN_COURS';
}

export function getFiliereInscriptionAvailability(
  filiereId: string,
  cohortes: FiliereCohorteAvailabilityRow[] | null | undefined,
): FiliereAvailability {
  const mine = (cohortes ?? []).filter(
    (c) => c.idFiliere === filiereId || c.filiere?.id === filiereId,
  );
  const inscritables = mine.filter((c) => isCohorteOpenForInscription(c.statut));
  const avecPlaces = inscritables.filter((c) => !isCohorteFull(c));
  const completes = inscritables.filter((c) => isCohorteFull(c));

  if (avecPlaces.length > 0) {
    const places = avecPlaces.reduce((sum, c) => sum + (c.capacity?.placesRestantes ?? 0), 0);
    if (places > 0) {
      return {
        status: 'open',
        label: `${places} place${places > 1 ? 's' : ''} disponible${places > 1 ? 's' : ''}`,
      };
    }
    return { status: 'open', label: 'Inscriptions ouvertes' };
  }

  if (completes.length > 0) {
    return { status: 'closed', label: 'Plus de places disponibles' };
  }

  return { status: 'closed', label: 'Inscriptions fermées' };
}

export function availabilityBadgeClasses(status: FiliereAvailabilityStatus): string {
  return status === 'open'
    ? 'bg-emerald-100 text-emerald-800 ring-emerald-300'
    : 'bg-red-50 text-red-800 ring-red-200';
}

/** Synthèse inscription pour une famille de filières (hero page track). */
export function getTrackInscriptionAvailability(
  filiereIds: string[],
  cohortes: FiliereCohorteAvailabilityRow[] | null | undefined,
): FiliereAvailability {
  if (filiereIds.length === 0) {
    return { status: 'closed', label: 'Inscriptions fermées' };
  }

  const statuses = filiereIds.map((id) => getFiliereInscriptionAvailability(id, cohortes));
  const openCount = statuses.filter((s) => s.status === 'open').length;
  const fullOnly =
    statuses.length > 0 && statuses.every((s) => s.label === 'Plus de places disponibles');

  if (openCount === 0) {
    if (fullOnly) {
      return { status: 'closed', label: 'Plus de places disponibles' };
    }
    return { status: 'closed', label: 'Inscriptions fermées' };
  }
  if (openCount === filiereIds.length) {
    return { status: 'open', label: 'Inscriptions ouvertes' };
  }
  return {
    status: 'open',
    label: `Inscriptions ouvertes sur ${openCount}/${filiereIds.length} parcours`,
  };
}
